import { inquiryReviewModelDict } from '@/dictionary/project/inquiry'
import {
  inquiryReviewTypeDict,
  onlineReviewCatalogStatusDict,
  onlineReviewCatalogTypeDict,
  onlineReviewMethodDict,
  onlineReviewScoreStatusDict
} from '@/dictionary/project/online-review'
import { getInquiryReviewRankPageApi } from '@/api/procure/inquiry-review'
import { getNegotiationReviewRankPageApi } from '@/api/procure/negotiation-review'
import { format } from 'date-fns'
import { generateTableHtml } from '@/utils/generate-html'
import {
  getPurchaseReviewExpertSummaryPageApi,
  getPurchaseReviewItemInfoAPi,
  getPurchaseReviewItemSupplierScoreApi
} from '@/api/procure/purchase-review'
import { arrToObj, getBase64FromImgUrl } from '@/utils/utils'
import { currencyTypeDict, purchaseTypeDict } from '@/dictionary/project/common'
import { sortBy } from 'lodash'
import { getFileUrlByFileIdApi } from '@/api/up-file'
import he from 'he'
import { getReportInHtmlTag } from '@/utils/generate-html/report-word'
import Decimal from 'decimal.js'

/**
 * 获取叶节点目录设置
 */
const getCatalogConfig = ({
  reportName,
  reportType = 0,
  wayId,
  wayType = 0,
  reviewerId = 0,
  attachmentId,
  parentKey = '',
  getReport
}) => {
  return {
    catalogKey: JSON.stringify({
      wayType,
      reportType,
      reportName,
      reviewerId
    }),
    reportName,
    reportType,
    catalogStatus: onlineReviewCatalogStatusDict.INIT.value,
    wayType,
    wayId,
    reviewerId,
    attachmentId,
    parentKey: parentKey,
    parentCatalogName: parentKey ? JSON.parse(parentKey)?.reportName : '',
    getReport
  }
}

/**
 * 获取父节点目录配置
 */
const getCatalogParentConfig = ({
  reportName,
  wayType = 0,
  reviewerList,
  getReport,
  wayId = 0,
  children = []
}) => {
  const catalogKey = JSON.stringify({
    wayType,
    reportName
  })
  return {
    catalogKey,
    reportName,
    catalogStatus: onlineReviewCatalogStatusDict.INIT.value,
    wayType: wayType || 0,
    children: children.length
      ? children.map(item => getCatalogConfig(item))
      : reviewerList.map(item =>
        getCatalogConfig({
          reportName: item.expertName,
          reportType: onlineReviewCatalogTypeDict.WAY_ITEM.value,
          wayType,
          parentKey: catalogKey,
          getReport,
          wayId,
          reviewerId: item.reviewerId
        })
      )
  }
}

// 根据wayType找到相应汇总信息
const getSummaryItem = (wayType, summaryList) => {
  return summaryList.find(el => el.wayType === wayType) || {}
}

const getReportCatalogList = (
  reviewModel,
  isReviewClose = false,
  summaryList = [],
  purchaseType,
  reviewerList = []
) => {
  // 评审报告
  const wordCatalog = getCatalogConfig({
    reportName: '评审报告',
    reportType: onlineReviewCatalogTypeDict.WORD.value,
    getReport: getWordReport
  })
  // 综合评分排序表
  const rankCatalog = !isReviewClose
    ? getCatalogConfig({
      reportName: '综合评分排序表',
      reportType: onlineReviewCatalogTypeDict.RANK.value,
      getReport: getRankReport
    })
    : undefined
  // 最低价排序表
  const lowestRankCatalog = !isReviewClose
    ? getCatalogConfig({
      reportName: '最低报价排序表',
      reportType: onlineReviewCatalogTypeDict.RANK.value,
      getReport: getRankReport
    })
    : undefined

  // 初步评审汇总
  const qualificationSummaryItem = getSummaryItem(
    inquiryReviewTypeDict.QUALIFICATION.value,
    summaryList
  )
  // 初步评审汇总表
  const qualificationCatalog =
    qualificationSummaryItem.summaryStatus ===
    onlineReviewScoreStatusDict.COMPLETED.value
      ? getCatalogConfig({
        reportName: inquiryReviewTypeDict.QUALIFICATION.label + '汇总表',
        reportType: onlineReviewCatalogTypeDict.WAY_SUMMARY.value,
        wayType: inquiryReviewTypeDict.QUALIFICATION.value,
        getReport: getWaySummaryReport,
        wayId: qualificationSummaryItem.wayId
      })
      : undefined

  // 综合评审汇总
  const businessSummaryItem = getSummaryItem(
    inquiryReviewTypeDict.BUSINESS.value,
    summaryList
  )
  // 综合评审汇总表
  const businessCatalog =
    businessSummaryItem.summaryStatus ===
    onlineReviewScoreStatusDict.COMPLETED.value
      ? getCatalogConfig({
        reportName: inquiryReviewTypeDict.BUSINESS.label + '汇总表',
        reportType: onlineReviewCatalogTypeDict.WAY_SUMMARY.value,
        wayType: inquiryReviewTypeDict.BUSINESS.value,
        getReport: getWaySummaryReport,
        wayId: businessSummaryItem.wayId
      })
      : undefined
  // 综合评审明细表
  const businessReviewerCatalog =
    businessSummaryItem.summaryStatus ===
    onlineReviewScoreStatusDict.COMPLETED.value
      ? getCatalogParentConfig({
        reportName: inquiryReviewTypeDict.BUSINESS.label + '专家明细表',
        wayType: inquiryReviewTypeDict.BUSINESS.value,
        reviewerList,
        getReport: getWayMemberDetailReport,
        wayId: businessSummaryItem.wayId
      })
      : undefined

  // 价格评审汇总
  const quotationSummaryItem = getSummaryItem(
    inquiryReviewTypeDict.QUOTATION.value,
    summaryList
  )
  // 价格评审汇总表
  const quotationCatalog =
    quotationSummaryItem.summaryStatus ===
    onlineReviewScoreStatusDict.COMPLETED.value
      ? getCatalogConfig({
        reportName: inquiryReviewTypeDict.QUOTATION.label + '汇总表',
        reportType: onlineReviewCatalogTypeDict.WAY_SUMMARY.value,
        wayType: inquiryReviewTypeDict.QUOTATION.value,
        getReport: getWaySummaryReport,
        wayId: quotationSummaryItem.wayId
      })
      : undefined

  // 综合评分法目录：综合评分排序表，初步评审汇总表，综合打分汇总表，价格分打分汇总表，评审报告
  // 最低价法目录：最低报价排序表，初步评审汇总表，评审报告
  // 目前线上评审所有报告都需要签名，所以暂时不做处理
  // 询比评审报告中，综合评审需要增加专家明细表
  const reportCatalogMap = {
    // 询比评审报告列表
    [purchaseTypeDict.INQUIRY.value]: {
      [inquiryReviewModelDict.SYNTHESIS.value]: [
        wordCatalog,
        rankCatalog,
        qualificationCatalog,
        businessCatalog,
        businessReviewerCatalog,
        quotationCatalog
      ].filter(value => value),
      [inquiryReviewModelDict.LOWEST_MARK.value]: [
        wordCatalog,
        lowestRankCatalog,
        qualificationCatalog
      ].filter(value => value)
    },
    // 谈判采购评审报告列表
    [purchaseTypeDict.NEGOTIATION.value]: {
      [inquiryReviewModelDict.SYNTHESIS.value]: [
        wordCatalog,
        rankCatalog,
        qualificationCatalog,
        businessCatalog,
        quotationCatalog
      ].filter(value => value),
      [inquiryReviewModelDict.LOWEST_MARK.value]: [
        wordCatalog,
        lowestRankCatalog,
        qualificationCatalog
      ].filter(value => value)
    }
  }

  const purchaseTypeCatalogMap = reportCatalogMap[purchaseType] || {}
  const catalogList = purchaseTypeCatalogMap[reviewModel] || []
  const resultList = []
  catalogList.forEach(item => {
    if (item.children) {
      resultList.push(...item.children)
    } else {
      resultList.push(item)
    }
  })
  return resultList
}

/**
 * 获取综合排序表的pdf模板
 * @param {Object} order 询比项目信息
 * @param {Object} reviewProject 评审项目信息
 * @param {Object} catalogConfig 目录设置信息
 * @param {Number} systemMillisecond 系统时间
 * @param purchaseType
 * @returns {Promise<unknown>}
 */
const getRankReport = ({
  order,
  reviewProject,
  catalogConfig,
  systemMillisecond,
  purchaseType = purchaseTypeDict.INQUIRY.value
}) => {
  const reviewId = reviewProject.reviewId
  const orderId = order.orderId
  const currencyTypeName =
    currencyTypeDict.translate(order.currencyType)?.label ||
    currencyTypeDict.YUAN.label
  // 是否是最低价法
  const isRankByPrice =
    reviewProject.reviewInfo?.reviewModel ===
    inquiryReviewModelDict.LOWEST_MARK.value

  return new Promise((resolve, reject) => {
    const objApi = {
      [purchaseTypeDict.INQUIRY.value]: getInquiryReviewRankPageApi,
      [purchaseTypeDict.NEGOTIATION.value]: getNegotiationReviewRankPageApi
    }
    objApi[purchaseType](reviewId, { orderId, isListing: 1 })
      .then(res => {
        const summaryList = sortBy(res.data?.records || [], 'rankNum')
        // 是否有未含税价
        const hasNoRateQuote = !!order.offerRequireTax
        // 获取评审办法列表
        const wayListHeader = isRankByPrice
          ? []
          : (reviewProject.wayInfoList || [])
            .filter(item => {
              // 综合排序表不展示初步评审
              return (
                item.wayType !== inquiryReviewTypeDict.QUALIFICATION.value
              )
            })
            .map(item => {
              return {
                content: inquiryReviewTypeDict.translate(item.wayType).label,
                wayId: item.wayId,
                reviewMethod: item.reviewMethod
              }
            })
        // 创建表头
        const tableHeader = [
          { content: '序号', width: '50px' },
          { content: '报价单位' },
          ...wayListHeader,
          { content: `报价总金额（${currencyTypeName}）` },
          hasNoRateQuote
            ? { content: `未含税价（${currencyTypeName}）` }
            : null,
          isRankByPrice ? null : { content: '评审总分' },
          { content: '排序' }
        ].filter(value => value)
        // 创建表格数据
        const tableData = summaryList.map((bidder, index) => {
          const itemMap = isRankByPrice
            ? {}
            : arrToObj(JSON.parse(bidder.summaryItems || '') || [], 'wayId')
          return tableHeader.map(header => {
            if (header.content === '序号') {
              return {
                content: index + 1
              }
            }
            if (header.content === '报价单位') {
              return {
                content: bidder.supplierName
              }
            }
            if (header.content === '排序') {
              return {
                content: bidder.rankNum
              }
            }
            if (header.content === `报价总金额（${currencyTypeName}）`) {
              return { content: bidder.offerPrice }
            }
            if (header.content === `未含税价（${currencyTypeName}）`) {
              return { content: bidder.noRateOfferPrice }
            }
            if (header.content === '评审总分') {
              return { content: bidder.summarySore }
            }
            if (header.wayId) {
              const isScore =
                header.reviewMethod === onlineReviewMethodDict.SCORE.value
              if (isScore) {
                return { content: itemMap[header.wayId]?.score || '-' }
              }
            }
            return {
              content: '-'
            }
          })
        })
        // 获取顶部描述内容
        const describeInfoTop = [
          [
            {
              label: '项目名称',
              content: order.orderName
            },
            {
              label: '项目编号',
              content: order.orderCode,
              align: 'right'
            }
          ]
        ]

        getSignHtml(reviewProject)
          .then(signHtml => {
            // 获取底部描述内容
            const describeInfoBottom = [
              [
                {
                  label: '全体专家签名',
                  html: signHtml,
                  style: 'width: 80%;'
                },
                {
                  label: '时间',
                  content: format(
                    new Date(systemMillisecond),
                    'yyyy-MM-dd HH:mm:ss'
                  ),
                  align: 'right'
                }
              ]
            ]
            const pdfTitle = catalogConfig.reportName
            const pdfTemplate = generateTableHtml({
              pdfTitle,
              describeInfoTop,
              describeInfoBottom,
              tableHead: {
                fixedPrev: tableHeader,
                dynamic: []
              },
              tableData,
              htmlConfig: {
                bodyFontSize: '12px'
              },
              needHtmlWrapper: true
            })
            resolve(pdfTemplate)
          })
          .catch(err => reject(err))
      })
      .catch(err => reject(err))
  })
}

/**
 * 获取某个评审办法下的评审点汇总表
 * @param {Object} order 询比项目信息
 * @param {Object} reviewProject 评审项目信息
 * @param {Object} reviewWay 评审办法信息
 * @param {Object} catalogConfig 目录设置信息
 * @param {Number} systemMillisecond 系统时间
 * @returns {Promise<unknown>}
 */
const getWayItemSummaryReport = ({
  order,
  reviewProject,
  reviewWay,
  catalogConfig,
  systemMillisecond
}) => {
  const reviewId = reviewProject.reviewId
  return new Promise((resolve, reject) => {
    Promise.allSettled([
      getPurchaseReviewItemInfoAPi(reviewId, { wayId: reviewWay.wayId }),
      getPurchaseReviewItemSupplierScoreApi(reviewId, {
        wayId: reviewWay.wayId,
        isListing: 1
      })
    ])
      .then(resList => {
        const wayItemRes = resList[0].value || {}
        const _data = wayItemRes.data || []
        // 是否是打分评审
        const isScore =
          reviewWay.reviewMethod === onlineReviewMethodDict.SCORE.value
        // 获取固定表头
        const fixedPrev = [
          { content: '序号', rowspan: '2', width: '50px' },
          { content: '评分点', rowspan: '2', width: '150px' },
          { content: '评分标准', rowspan: '2' }
        ]
        // _data是所有评审点的打分信息集合，从下标为0的数据中获取供应商列表
        const supplierList = _data[0]?.scoreItemList || []
        // 获取报价单位表头
        const bidderHeader = supplierList.map(supplier => {
          return {
            content: supplier.supplierName,
            supplierId: supplier.supplierId
          }
        })
        // 获取表头
        const tableHeader = [
          [
            {
              content: '报价单位名单及评审意见',
              colspan: supplierList.length
            }
          ],
          bidderHeader
        ]
        // 获取单个评审办法下，各个供应商的总分信息
        const waySummaryRes = resList[1].value || {}
        const summaryList = waySummaryRes.data?.records || []
        const summaryMap = arrToObj(summaryList, 'supplierId')
        // 获取表格内容
        const tableData = (_data || []).map((item, index) => {
          const scoreItemList = item.scoreItemList || []
          const supplierMap = arrToObj(scoreItemList, 'supplierId')
          return [
            { content: index + 1 },
            { content: item.itemName },
            { content: item.itemStandard },
            ...bidderHeader.map(head => {
              const scoreInfo = supplierMap[head.supplierId]
              return {
                content: isScore
                  ? scoreInfo?.score
                  : Number(scoreInfo?.score || 0)
                    ? '通过'
                    : '不通过'
              }
            })
          ]
        })
        tableData.push([
          { content: _data.length + 1 },
          { content: '合计', colspan: 2 },
          ...bidderHeader.map(head => {
            const scoreInfo = summaryMap[head.supplierId]
            return {
              content: isScore
                ? scoreInfo?.score
                : Number(scoreInfo?.score || 0)
                  ? '通过'
                  : '不通过'
            }
          })
        ])
        // 获取顶部描述内容
        const describeInfoTop = [
          [
            {
              label: '项目名称',
              content: order.orderName
            },
            {
              label: '项目编号',
              content: order.orderCode,
              align: 'right'
            }
          ]
        ]

        getSignHtml(reviewProject)
          .then(signHtml => {
            // 获取底部描述内容
            const describeInfoBottom = [
              [
                {
                  label: '全体专家签名',
                  html: signHtml,
                  style: 'width: 80%;'
                },
                {
                  label: '时间',
                  content: format(
                    new Date(systemMillisecond),
                    'yyyy-MM-dd HH:mm:ss'
                  ),
                  align: 'right'
                }
              ]
            ]
            const pdfTitle = catalogConfig.reportName
            const pdfTemplate = generateTableHtml({
              pdfTitle,
              describeInfoTop,
              describeInfoBottom,
              tableHead: {
                fixedPrev: fixedPrev,
                fixedLast: [],
                dynamic: tableHeader,
                dynamicNum: 5
              },
              tableData,
              tableConfig: {
                // 需要进行表格内容切分
                needTableSplit: true,
                contentMaxLength: 125
              },
              needHtmlWrapper: true
            })
            resolve(pdfTemplate)
          })
          .catch(err => reject(err))
      })
      .catch(err => reject(err))
  })
}

/**
 * 获取某个评审办法下的专家汇总表
 * @param {Object} order 询比项目信息
 * @param {Object} reviewProject 评审项目信息
 * @param {Object} reviewWay 评审办法信息
 * @param {Object} catalogConfig 目录设置信息
 * @param {Number} systemMillisecond 系统时间
 * @returns {Promise<unknown>}
 */
const getWayMemberSummaryReport = ({
  order,
  reviewProject,
  reviewWay,
  catalogConfig,
  systemMillisecond
}) => {
  const reviewId = reviewProject.reviewId
  return new Promise((resolve, reject) => {
    getPurchaseReviewExpertSummaryPageApi(reviewId, {
      wayId: reviewWay.wayId,
      isListing: 1
    })
      .then(res => {
        const _data = res.data.records || []
        // 获取固定表头
        const fixedPrev = [
          { content: '序号', width: '50px' },
          { content: '投标单位' }
        ]
        // 是否是打分评审
        const isScore =
          reviewWay.reviewMethod === onlineReviewMethodDict.SCORE.value
        const tableHeader = reviewProject.reviewerInfoList?.map(item => {
          return {
            reviewerId: item.reviewerId,
            content: item.expertName
          }
        })
        tableHeader.push({ content: isScore ? '汇总' : '结论' })
        // 获取表格数据
        const tableData = _data.map((bidder, index) => {
          const reviewerSummaryMap = arrToObj(
            bidder.summaryItems || [],
            'reviewerId'
          )
          return [...fixedPrev, ...tableHeader].map(header => {
            if (header.content === '序号') {
              return { content: index + 1 }
            }
            if (header.content === '投标单位') {
              return { content: bidder.supplierName || '-' }
            }
            if (header.content === '汇总' && isScore) {
              const totalScore = (bidder.summaryItems || []).reduce(
                (acc, cur) => {
                  return Decimal.add(acc, Number(cur.score)).toNumber()
                },
                0
              )
              return { content: totalScore || '-' }
            }
            if (header.content === '结论') {
              return { content: Number(bidder.score) ? '通过' : '不通过' }
            }
            if (header.reviewerId) {
              const result = reviewerSummaryMap[header.reviewerId]
              const passResult = Number(result.score) ? '通过' : '不通过'
              const scoreResult = result.score || '-'
              return {
                content: isScore ? scoreResult : passResult
              }
            }
            return { content: '-' }
          })
        })
        // 获取顶部描述内容
        const describeInfoTop = [
          [
            {
              label: '项目名称',
              content: order.orderName
            },
            {
              label: '项目编号',
              content: order.orderCode,
              align: 'right'
            }
          ]
        ]
        getSignHtml(reviewProject)
          .then(signHtml => {
            // 获取底部描述内容
            const describeInfoBottom = [
              [
                {
                  label: '全体专家签名',
                  html: signHtml,
                  style: 'width: 80%;'
                },
                {
                  label: '时间',
                  content: format(
                    new Date(systemMillisecond),
                    'yyyy-MM-dd HH:mm:ss'
                  ),
                  align: 'right'
                }
              ]
            ]
            const pdfTitle = catalogConfig.reportName
            const pdfTemplate = generateTableHtml({
              pdfTitle,
              describeInfoTop,
              describeInfoBottom,
              tableHead: {
                fixedPrev: fixedPrev,
                fixedLast: [],
                dynamic: tableHeader,
                dynamicNum: 5
              },
              tableData,
              tableConfig: {
                // 需要进行表格内容切分
                needTableSplit: true,
                contentMaxLength: 200
              },
              needHtmlWrapper: true
            })
            resolve(pdfTemplate)
          })
          .catch(err => reject(err))
      })
      .catch(err => reject(err))
  })
}

/**
 * 获取某个评审办法下某个专家的评审明细表
 */
const getWayMemberDetailReport = ({
  order,
  reviewProject,
  reviewWay,
  catalogConfig,
  systemMillisecond,
  reviewer
}) => {
  const reviewId = reviewProject.reviewId
  return new Promise((resolve, reject) => {
    getPurchaseReviewItemSupplierScoreApi(reviewId, {
      wayId: reviewWay.wayId,
      isListing: 1,
      reviewerId: reviewer.reviewerId
    }).then(res => {
      const _data = res.data?.records || []
      // 是否是打分评审
      const isScore =
        reviewWay.reviewMethod === onlineReviewMethodDict.SCORE.value
      // 获取表头
      const tableHeader = [
        { content: '评审要点', colspan: 2 },
        ..._data.map(bidder => {
          return {
            content: bidder.supplierName,
            supplierId: bidder.supplierId,
            score: bidder.score,
            wayItemScore: arrToObj(JSON.parse(bidder.itemScores), 'wayItemId')
          }
        })
      ]
      const rowList = [...reviewWay.itemInfoList, { itemName: '合计' }]
      // 获取表格数据
      const tableData = rowList.map(wayItem => {
        return tableHeader
          .map(header => {
            if (header.content === '评审要点') {
              if (wayItem.itemStandard) {
                return [
                  { content: wayItem.itemName, style: 'width: 100px' },
                  { content: wayItem.itemStandard, style: 'width: 220px' }
                ]
              }
              return { content: wayItem.itemName, colspan: 2 }
            }
            if (header.supplierId) {
              const itemMap = header.wayItemScore || {}
              const reviewResult = itemMap[wayItem.wayItemId] || {}
              if (isScore) {
                if (wayItem.itemStandard) {
                  return { content: reviewResult.score || '-' }
                }
                return { content: header.score || '-' }
              } else {
                return { content: reviewResult.score ? '通过' : '不通过' }
              }
            }
            return { content: '-' }
          })
          .flat()
      })
      // 设置固定的左浮动表头
      const fixedPrev = tableHeader.splice(0, 1)
      // 获取顶部描述内容
      const describeInfoTop = [
        [
          {
            label: '项目名称',
            content: order.orderName
          },
          {
            label: '项目编号',
            content: order.orderCode,
            align: 'right'
          }
        ]
      ]
      getSignHtml(reviewProject)
        .then(signHtml => {
          // 获取底部描述内容
          const describeInfoBottom = [
            [
              {
                label: '全体专家签名',
                html: signHtml,
                style: 'width: 80%;'
              },
              {
                label: '时间',
                content: format(
                  new Date(systemMillisecond),
                  'yyyy-MM-dd HH:mm:ss'
                ),
                align: 'right'
              }
            ]
          ]
          const pdfTitle =
            reviewer.expertName +
            '专家' +
            inquiryReviewTypeDict.translate(reviewWay.wayType).label +
            '打分表'
          const pdfTemplate = generateTableHtml({
            pdfTitle,
            describeInfoTop,
            describeInfoBottom,
            tableHead: {
              fixedPrev: fixedPrev,
              fixedLast: [],
              dynamic: tableHeader,
              dynamicNum: 5
            },
            tableData,
            tableConfig: {
              // 需要进行表格内容切分
              needTableSplit: true,
              contentMaxLength: 650
            },
            needHtmlWrapper: true
          })
          resolve(pdfTemplate)
        })
        .catch(err => reject(err))
    })
  })
}

const getWaySummaryReport = params => {
  // 判断评审办法类型，初步评审使用评审点汇总
  const needMemberReview =
    params.reviewWay.wayType === inquiryReviewTypeDict.BUSINESS.value
  if (needMemberReview) {
    return getWayMemberSummaryReport(params)
  }
  return getWayItemSummaryReport(params)
}

const getSignHtml = async reviewProject => {
  const signList = (reviewProject.reviewerInfoList || []).filter(
    item => item.signFileId
  )
  // 获取签名列表
  let signHtml = ''
  for (let i = 0; i < signList?.length; i++) {
    const signItem = signList[i]
    if (signItem?.signFileId) {
      const fileRes = await getFileUrlByFileIdApi({
        attachmentId: signItem?.signFileId
      })
      const imgRes = await getBase64FromImgUrl(fileRes.data.external)
      signHtml += `<img src="${imgRes}" style="width:100px;height:50px;margin-right: 8px; margin-bottom: 8px;" />`
    }
  }
  return `<div class="sign-list">${signHtml}</div>`
}

const getWordReport = async({ reviewProject, reportWordTemplate }) => {
  const signHtml = await getSignHtml(reviewProject)

  let wordTemplate = reportWordTemplate
    .replace(/_ueditor_page_break_tag_/g, '')
    .replace(/【签名区域】/g, signHtml)
  wordTemplate = he.encode(wordTemplate.replace(/&nbsp;/g, ''), {
    allowUnsafeSymbols: true
  })
  return getReportInHtmlTag(wordTemplate).replace(/&&/g, '&')
}

const useOnlineReviewReport = () => {
  return {
    getCatalogConfig,
    getCatalogParentConfig,
    getReportCatalogList
  }
}

export default useOnlineReviewReport
